import { useCallback, useMemo, useState } from "react";

import type { ProductOptions } from "@/api/import/products";

// Products have a fixed shape, so the form is plain local state rather than the
// server-generated field list the vendored resources (sources, destinations) use.
export type ProductDraft = {
  name: string;
  description: string;
  destinations: string[];
};

const EMPTY_PRODUCT_DRAFT: ProductDraft = {
  name: "",
  description: "",
  destinations: [],
};

export type UseProductFormResult = {
  draft: ProductDraft;
  setField: <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) => void;
  toggleDestination: (destination: string, checked: boolean) => void;
  isValid: boolean;
  getProduct: () => ProductOptions;
};

export function useProductForm(): UseProductFormResult {
  const [draft, setDraft] = useState<ProductDraft>(EMPTY_PRODUCT_DRAFT);

  const setField = useCallback(<K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  }, []);

  const toggleDestination = useCallback((destination: string, checked: boolean) => {
    setDraft((current) => ({
      ...current,
      destinations: checked
        ? [...current.destinations, destination]
        : current.destinations.filter((d) => d !== destination),
    }));
  }, []);

  // Destinations are optional: the system `all` product is created without any.
  const isValid = useMemo(() => !!draft.name.trim(), [draft]);

  const getProduct = useCallback(
    (): ProductOptions => ({
      name: draft.name.trim(),
      description: draft.description.trim(),
      destinations: draft.destinations,
    }),
    [draft],
  );

  return { draft, setField, toggleDestination, isValid, getProduct };
}
